/**
 * ATL-1445 stage 3, P4 — WRITE-MODE RESOLUTION: which store is this session's write master?
 *
 * Design: `docs/technical/write-cutover-stage3-app-seam.md` §1 (the seam's two modes, the coverage
 * manifest rule) and §4 ("Reading the flag at startup"). Pure functions only: no Firebase, no
 * storage, no clock. The startup read lives in `global/Firebase/orgReadCutover.ts`, the persisted
 * memory in `writesMasterMemory.ts`, and the session pin in `writeW.ts` (the seam owns its mode).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE FLAG, AND WHICH PLANE THE CLIENT READS
 *
 * `writes_master` lives on two planes (`write-cutover-fences.md` "The flag"): the Supabase column
 * `organizations.writes_master` and the Firestore field `organizations/{id}.writesMaster`. The client
 * gates on the FIRESTORE plane only, exactly as the read flag does (ATL-886): the organization
 * document is readable before any data source is chosen, and a Supabase-copy-only change is not a
 * flip for the client (ATL-886's "cutovers" were no-ops for precisely that reason). An absent field
 * or document means `'firestore'` (every organization before its cutover — the stage-3 setter
 * refuses to flip an organization whose Firestore document does not exist); any value outside the
 * two is UNREADABLE, never a guess (`asWriteMaster` on the backend, the same rule).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THE WRITE FLAG IS REMEMBERED WHEN THE READ FLAG IS NOT
 *
 * `orgReadCutover.ts` argues against caching the READ flag: a cached value would delay a rollback,
 * and an unreadable flag must resolve to the store nobody has to clear (Firestore). For the WRITE
 * flag the risky direction is the other one (§4). A till that believes a cut-over organization is
 * Firestore-master writes Firestore-first: a second master of the same stock and money, the ATL-707
 * corruption class. A till that believes a Firestore-master organization is Supabase-master only
 * queues (outbox v2, P3) — the backend applies master-header writes on a Firestore-master
 * organization and reverse-mirrors them (backend PR B1). So:
 *
 *   - a SERVER-confirmed read is the truth in either direction, and is remembered;
 *   - a CACHED read (Firestore's local cache, `metadata.fromCache`) may RAISE the answer to
 *     `'supabase'` but may never LOWER a remembered `'supabase'`: a cache is a past server answer of
 *     unknown age, and the rollback it might reflect is exactly the one a stale cache would fake;
 *   - an UNREADABLE flag keeps the last known value; with no memory it defaults to `'firestore'`,
 *     which is today's behaviour for every organization and the read side's default too.
 *
 * That last default is a deliberate, stated limit, not a claim of safety: a device that has NEVER
 * seen its organization's flag and cannot read it now writes Firestore-first. A device can only be in
 * that state if it has not started online since the flip (signing in needs the network), and the
 * `write-bundle-coverage` gate clause requires every device of the organization to have reported
 * before the flip; the backend's 409 on a stray Firestore-first mirror (B1, converted by P8) is the
 * backstop for what remains.
 *
 * THE READ/WRITE FALLBACK ASYMMETRY IS A DESIGNED OUTCOME (#929 review 1, S2). When the organization
 * document cannot be read, READS fall back to Firestore (ATL-886's rule: an unreadable flag is not
 * consent to move a merchant onto another database) while WRITES keep a remembered `'supabase'`. For
 * a cut-over organization that session therefore reads Firestore with a Supabase write master — the
 * §4 invalid state — and lands in `refuse`: no W write at all, rather than a second master. On web,
 * where nothing of the organization document survives a cold start offline, that is EVERY offline
 * cold start of a cut-over organization (native has the Firestore cache, read as `cache` provenance,
 * which still says `'supabase'`). This is accepted, not a defect: but a till that silently refuses
 * every sale is not acceptable either, so a VISIBLE notice ("нет связи — продажи будут доступны после
 * подключения" or equivalent) is REQUIRED before any organization is flipped. Owned by P5 (the watcher
 * re-pins when the document becomes readable) and P9 (the notice, on the refusal path the cashier sees).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * FROM THE ORGANIZATION'S MASTER TO THE SESSION'S MODE (§1)
 *
 *   - `'firestore'` master → Firestore mode: `writeW` runs today's Firestore-first body, byte for byte.
 *     EXCEPT (P5) when startup found outbox v2 still holding this organization's master writes, or
 *     could not read it: lowering to Firestore-first on top of undelivered master writes would be a
 *     second master and would break their causal order. The session then keeps queueing
 *     (`outbox-v2-draining`, capable bundle reading Supabase) or refuses (`outbox-v2-not-drained`),
 *     and the live watcher's drain-to-empty rollback (`writeCutoverWatch.ts`) restarts it once v2
 *     is empty. Startup only looks when v2 can hold anything (`shouldProbeOutboxV2AtStartup`), so
 *     for every organization today the row is unreachable and P4's path is unchanged.
 *   - `'supabase'` master, bundle NOT capable (`WRITE_W_CAPABILITY`: the compiled-in manifest does
 *     not show every W writer converted, or the seam has no backend-first branch) → REFUSE every W
 *     write, and report it. DECIDED in #929 review 1 (S1), overriding §1's original "stays in
 *     Firestore mode and reports why": a second master is the ATL-707 corruption class, a refusal is
 *     visible and recoverable. ⚠️ This mode binds only writers that call `writeW` — in an incapable
 *     bundle at least one W writer is unconverted and never consults it, so the decision is ENFORCED
 *     only by a production Firestore-write interceptor keyed on this pin (ATL-1601). That
 *     interceptor is the stage-3 gate `incapable-bundle-w-refusal` the `writes_master` setter's
 *     enable must require. The gate's `write-bundle-coverage` clause exists so that no device of a
 *     flipped organization is in this row at all.
 *   - `'supabase'` master, capable bundle, but this session reads FIRESTORE → REFUSE every W write.
 *     This is the invalid state §4 forbids ("writes = supabase while reads = firestore: never enter
 *     it, and alert"). Supabase mode's local view is an overlay on the API read model, so it cannot
 *     run over Firestore reads; and Firestore mode here would be a second master. Refusing is the
 *     only single-master answer. Unreachable while no bundle is capable (every bundle today).
 *   - `'supabase'` master, capable bundle, Supabase reads → Supabase mode (backend first, P9).
 *   - P9b, STAGING ONLY: a cut-over PREVIEW build (`writeWPreview.ts`, compiled out of production) is
 *     treated as able to go backend-first in the two rows above that need it — reason
 *     `org-supabase-master-preview` (and `outbox-v2-draining`) — while staying `capable: false`. In
 *     that mode its unconverted W writers refuse visibly (`assertWWriterAvailable`); with Firestore
 *     reads it still refuses (§4). For a Firestore-master organization with nothing in outbox v2 it
 *     decides exactly what any incapable bundle decides.
 *
 * Every row is a pure function of its inputs, so the whole table is tested exhaustively
 * (`global/Write/__tests__/writeMode.test.js`).
 */
import type { WriteWCapability } from './writeWCapability'

/** The organization's write master, as the client understands it. */
export type WritesMaster = 'firestore' | 'supabase'

export const WRITES_MASTERS: readonly WritesMaster[] = ['firestore', 'supabase']

/** Where a successfully read flag came from. Only `'server'` may lower a remembered `'supabase'`. */
export type WritesMasterProvenance = 'server' | 'cache'

/** Why the flag could not be read at startup. */
export type WritesMasterUnreadableWhy =
  /** No organization id was known, so nothing was read. */
  | 'no-org-id'
  /** The document read threw (offline with nothing cached, permissions, …). */
  | 'read-error'
  /** The document exists but carries no payload. */
  | 'empty-document'
  /** The field holds something other than `'firestore'`/`'supabase'` (or absent). */
  | 'invalid-value'

/** What the startup read of the organization document said about `writesMaster`. */
export type WritesMasterObservation =
  | { kind: 'read'; provenance: WritesMasterProvenance; value: WritesMaster }
  | { kind: 'unreadable'; why: WritesMasterUnreadableWhy }

/** What the device remembered from an earlier session (`writesMasterMemory.ts`). */
export type LastKnownWritesMaster =
  | { status: 'none' }
  | { status: 'known'; value: WritesMaster; observedAt: string }
  /** Storage failed or held something unparseable: nothing is known, and that is reported. */
  | { status: 'unreadable' }

/**
 * The field's value as a write master. An absent field (`undefined`/`null`) is `'firestore'`; any
 * other value outside the two is `null` — unreadable, never a guess. Mirrors the backend's
 * `firestorePlaneOf` (`apps/atlas-backend/src/common/utils/writes-master-planes.util.ts`).
 */
export const writesMasterOfField = (raw: unknown): WritesMaster | null => {
  if (raw === undefined || raw === null) return 'firestore'
  return raw === 'firestore' || raw === 'supabase' ? raw : null
}

/**
 * Why the resolved master is what it is:
 *  - `server`: a server-confirmed read;
 *  - `cache`: a cached read that nothing remembered contradicts;
 *  - `last-known`: the read was unreadable, or a cached `'firestore'` met a remembered `'supabase'`;
 *  - `default`: unreadable and nothing remembered — `'firestore'`, today's behaviour.
 */
export type WritesMasterBasis = 'server' | 'cache' | 'last-known' | 'default'

/** Conditions worth a human's attention. Stable strings: telemetry groups by them. */
export type WriteModeAlert =
  /** The startup read of the flag failed; the answer came from memory or the default. */
  | 'writes-master-unreadable'
  /** A cached `'firestore'` was overruled by a remembered `'supabase'` (a cache cannot lower it). */
  | 'cached-flag-cannot-lower-last-known'
  /** The device's memory of the flag could not be read. */
  | 'last-known-unreadable'
  /** This bundle cannot honour a Supabase-master organization: W writes are refused (S1; enforced by ATL-1601). */
  | 'incapable-bundle-on-supabase-master-org'
  /** §4's invalid state: writes Supabase-master while this session reads Firestore. */
  | 'writes-supabase-without-reads'
  /**
   * P5: the organization is Firestore-master but outbox v2 still holds this organization's master
   * writes (or could not be read). The session is NOT lowered to Firestore mode until v2 is empty
   * (§4 "Reads must not reload onto Firestore while v2 is non-empty"; §11 "P5 has to hold that line
   * across a reload").
   */
  | 'outbox-v2-not-drained'

export interface OrgWritesMasterResolution {
  writesMaster: WritesMaster
  basis: WritesMasterBasis
  /** The inputs, kept so the report can say exactly what was seen. */
  observation: WritesMasterObservation
  lastKnown: LastKnownWritesMaster
  /**
   * What `writesMasterMemory` should now remember, or null for "leave it". Only a server-confirmed
   * value is remembered in the lowering direction; a cached `'supabase'` may be remembered too (it
   * only ever raises). Nothing derived from memory or the default is written back.
   */
  remember: WritesMaster | null
  alerts: WriteModeAlert[]
}

/**
 * Combine the startup read with the device's memory (the rules in the module docblock). Total: every
 * combination of inputs has exactly one answer, and none of them throws.
 */
export const resolveOrgWritesMaster = (
  observation: WritesMasterObservation,
  lastKnown: LastKnownWritesMaster,
): OrgWritesMasterResolution => {
  const alerts: WriteModeAlert[] = []
  if (lastKnown.status === 'unreadable') alerts.push('last-known-unreadable')
  const remembered = lastKnown.status === 'known' ? lastKnown.value : null
  const answer = (
    writesMaster: WritesMaster,
    basis: WritesMasterBasis,
    remember: WritesMaster | null,
    extra: WriteModeAlert[] = [],
  ): OrgWritesMasterResolution => ({
    writesMaster,
    basis,
    observation,
    lastKnown,
    // Writing back what is already remembered is a no-op; skip the storage write.
    remember: remember === remembered ? null : remember,
    alerts: [...alerts, ...extra],
  })

  if (observation.kind === 'read') {
    if (observation.provenance === 'server') return answer(observation.value, 'server', observation.value)
    // A cached read: it may raise, never lower.
    if (observation.value === 'supabase') return answer('supabase', 'cache', 'supabase')
    if (remembered === 'supabase') return answer('supabase', 'last-known', null, ['cached-flag-cannot-lower-last-known'])
    return answer('firestore', 'cache', null)
  }

  if (remembered) return answer(remembered, 'last-known', null, ['writes-master-unreadable'])
  return answer('firestore', 'default', null, ['writes-master-unreadable'])
}

/** The session's effective write mode — what `writeW` does with every W write. */
export type WriteMode =
  /** Today's Firestore-first body, verbatim (pass-through). */
  | 'firestore'
  /** Backend first through outbox v2 (P3/P9). */
  | 'supabase'
  /** Every W write is refused: the single-master answer to a state this session cannot honour. */
  | 'refuse'

export type WriteModeReason =
  /** The organization is Firestore-master (every organization today). */
  | 'org-firestore-master'
  /** Supabase-master organization, capable bundle, Supabase reads. */
  | 'org-supabase-master'
  /**
   * P9b, STAGING ONLY: Supabase-master organization, a cut-over PREVIEW build (incapable, see
   * `writeWPreview.ts`), Supabase reads. Supabase mode for the converted writers; every unconverted W
   * writer refuses visibly (`assertWWriterAvailable`). Unreachable in a production build.
   */
  | 'org-supabase-master-preview'
  /** Supabase-master organization, but this bundle cannot honour it: refuse (decision S1, #929). */
  | 'bundle-incapable'
  /** Supabase-master organization, capable bundle, Firestore reads: §4's invalid state. */
  | 'writes-supabase-without-reads'
  /**
   * P5: Firestore-master organization, outbox v2 not drained, capable bundle, Supabase reads:
   * trading continues into v2 (backend PR B1 applies a master-header write on a Firestore-master
   * organization and reverse-mirrors it), and the watcher's drain-to-empty rollback restarts the
   * session onto Firestore once v2 is empty.
   */
  | 'outbox-v2-draining'
  /** P5: Firestore-master organization, outbox v2 not drained, and Supabase mode unavailable: refuse. */
  | 'outbox-v2-not-drained'

/**
 * What the session knows about this organization's outbox v2 queue on this device at startup (P5).
 *  - `not-checked`: the queue was not looked at, because it cannot hold anything
 *    (`shouldProbeOutboxV2AtStartup`: no remembered `'supabase'` and an incapable bundle). Every
 *    organization today.
 *  - `empty` / `non-empty`: listed.
 *  - `unknown`: it had to be looked at and the listing failed or timed out. Treated as non-empty:
 *    a Firestore-first write on top of undelivered master writes is a second master.
 */
export type OutboxV2Presence = 'not-checked' | 'empty' | 'non-empty' | 'unknown'

/** Whether the presence forbids lowering the session (or the memory) to Firestore. */
export const outboxV2BlocksFirestore = (presence: OutboxV2Presence): boolean =>
  presence === 'non-empty' || presence === 'unknown'

/**
 * Must startup look at outbox v2 before pinning (P5)? Only when the answer can matter and the queue
 * can hold this organization's entries.
 *
 * It can matter only when the organization resolves to `'firestore'`: `decideSessionWriteMode` reads
 * `outboxV2` on that branch alone. A Supabase-master resolution is pinned at once, never held behind
 * a store listing (review finding: holding it left an incapable bundle UNPINNED — Firestore mode — for
 * up to the probe timeout on a Supabase-master organization).
 *
 * The queue can hold entries only with:
 *  - a remembered `'supabase'`: a session on this device was, or was about to be, backend-first;
 *  - a capable bundle: it may have queued in an earlier session whatever the memory says (its
 *    `'supabase'` memory write may have failed).
 *
 * NOT for an unreadable memory on an incapable bundle (P5 resume review): no bundle that ever ran has been
 * capable, so on every device today v2 is empty by construction, and looking would only add a way
 * for a slow or failing store to pin a Firestore-master organization's session to `refuse` (a
 * listing that fails reads `unknown`, which blocks) — and for the live watcher to then restart it
 * onto Firestore, once per start, for as long as the probe keeps failing. An incapable bundle that
 * does not remember `'supabase'` therefore keeps P4's startup path exactly: no store opened, no
 * extra await.
 *
 * Residuals (stated, §14): a device that ran a capable bundle and then an incapable one (an OTA
 * capability downgrade) is not probed when its memory is unreadable or its `'supabase'` write
 * failed; the incapable bundle's unconverted writers write Firestore-first on such a device anyway
 * (S1: only ATL-1601 can stop them).
 */
export const shouldProbeOutboxV2AtStartup = (
  observation: WritesMasterObservation,
  lastKnown: LastKnownWritesMaster,
  capability: WriteWCapability,
): boolean =>
  resolveOrgWritesMaster(observation, lastKnown).writesMaster === 'firestore' &&
  (capability.capable || (lastKnown.status === 'known' && lastKnown.value === 'supabase'))

/**
 * When startup looks at outbox v2 (review 1, item 2):
 *  - `before-pin`: the answer can change the pin (`shouldProbeOutboxV2AtStartup`);
 *  - `after-pin`: the organization resolves to `'supabase'`. The pin cannot depend on v2 (it is
 *    Supabase or refuse either way) and is taken at once, but whatever v2 holds is master writes the
 *    cashier saw confirmed: they are drained by this session — `refuse` included — never left on disk
 *    until some later session happens to look. Covers a cold start after reads were rolled back while
 *    the app was closed (writes Supabase-master, reads Firestore ⇒ refuse), and an OTA capability
 *    downgrade (a capable session queued; this incapable one refuses);
 *  - `none`: a Firestore-master organization whose queue cannot hold anything — every organization
 *    today. No store is opened.
 */
export type StartupOutboxLook = 'before-pin' | 'after-pin' | 'none'

export const decideStartupOutboxLook = (
  observation: WritesMasterObservation,
  lastKnown: LastKnownWritesMaster,
  capability: WriteWCapability,
): StartupOutboxLook => {
  if (shouldProbeOutboxV2AtStartup(observation, lastKnown, capability)) return 'before-pin'
  return resolveOrgWritesMaster(observation, lastKnown).writesMaster === 'supabase' ? 'after-pin' : 'none'
}

export interface SessionWriteModeDecision {
  organizationId: string
  mode: WriteMode
  reason: WriteModeReason
  resolution: OrgWritesMasterResolution
  /** Whether this session reads Supabase — the read pin (`sessionReadsSupabaseRef`, ATL-888). */
  sessionReadsSupabase: boolean
  /** The capability the decision was taken with (the compiled-in constant in the app). */
  capability: WriteWCapability
  /** What startup knew about outbox v2 (P5); `not-checked` for every organization today. */
  outboxV2: OutboxV2Presence
  alerts: WriteModeAlert[]
}

/** The session's write mode from the organization's master, this bundle, and the session's reads. */
export const decideSessionWriteMode = ({
  organizationId,
  resolution,
  sessionReadsSupabase,
  capability,
  outboxV2 = 'not-checked',
}: {
  organizationId: string
  resolution: OrgWritesMasterResolution
  sessionReadsSupabase: boolean
  capability: WriteWCapability
  /** P5: omitted ⇒ `not-checked`, which is P4's table exactly. */
  outboxV2?: OutboxV2Presence
}): SessionWriteModeDecision => {
  const base = { organizationId, resolution, sessionReadsSupabase, capability, outboxV2 }
  if (resolution.writesMaster === 'firestore') {
    if (!outboxV2BlocksFirestore(outboxV2)) {
      return { ...base, mode: 'firestore', reason: 'org-firestore-master', alerts: [...resolution.alerts] }
    }
    // P5: undelivered master writes (or a queue that could not be read). Never Firestore-first on
    // top of them; keep queueing if this session can, refuse otherwise.
    const alerts: WriteModeAlert[] = [...resolution.alerts, 'outbox-v2-not-drained']
    if (canGoBackendFirst(capability) && sessionReadsSupabase) {
      return { ...base, mode: 'supabase', reason: 'outbox-v2-draining', alerts }
    }
    return { ...base, mode: 'refuse', reason: 'outbox-v2-not-drained', alerts }
  }
  const alerts: WriteModeAlert[] = [...resolution.alerts]
  if (!sessionReadsSupabase) alerts.push('writes-supabase-without-reads')
  if (!canGoBackendFirst(capability)) {
    alerts.push('incapable-bundle-on-supabase-master-org')
    return { ...base, mode: 'refuse', reason: 'bundle-incapable', alerts }
  }
  if (!sessionReadsSupabase) return { ...base, mode: 'refuse', reason: 'writes-supabase-without-reads', alerts }
  if (!capability.capable) return { ...base, mode: 'supabase', reason: 'org-supabase-master-preview', alerts }
  return { ...base, mode: 'supabase', reason: 'org-supabase-master', alerts }
}

/**
 * P9b — may this session go backend-first at all? A capable bundle, or a STAGING cut-over preview
 * build (`writeWPreview.ts`; `capability.preview` is compiled to false in every production build).
 * Used by `decideSessionWriteMode` ONLY. Everything else keeps reading `capable`:
 *  - `shouldProbeOutboxV2AtStartup`: a preview device that ever queued remembers `'supabase'` (the
 *    pin's memory write), which already makes startup look; an unremembered preview device has the
 *    same "v2 empty by construction" argument as any incapable bundle that never went backend-first;
 *  - the gate's capability event: a preview is never capable.
 * The one other reader of `preview` is the unpinned window (`currentSessionWriteMode` in `writeW.ts`):
 * a preview REFUSES before its pin, as a capable bundle does (P9b review 1, ATL-1678).
 */
const canGoBackendFirst = (capability: WriteWCapability): boolean => capability.capable || capability.preview === true
